import React, { useState, useEffect, useDeferredValue } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Refresh, CheckCircleOutline } from '@mui/icons-material';
import logo from '../images/logo.svg';
import { useNavigate } from 'react-router-dom';
import axios from '../../helpers/request';
import moment from 'moment';
import DateTimePicker from 'react-datetime-picker';
import styled from '@emotion/styled';

// Main container
const MainContainer = styled.main({
  flexGrow: 1,
  minHeight: '100vh',
  overflow: 'auto',
  backgroundColor: '#f8fafc',
  padding: 0
});

// Header bar
const HeaderBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e2e8f0',
});

const HeaderLeft = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const LogoImage = styled.img({
  height: "28px",
});

const LogoBox = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  backgroundColor: '#10416D',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 700,
});

const BrandName = styled.span({
  fontSize: '18px',
  fontWeight: 600,
  color: '#10416D',
  letterSpacing: '-0.5px',
});

const HeaderRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const HeaderTitle = styled.span({
  fontSize: '13px',
  fontWeight: 500,
  color: '#10416D',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

// Filter bar styles
const FilterBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 24px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e2e8f0',
  flexWrap: 'wrap',
  '& .react-datetime-picker__wrapper': {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
  },
  '& .react-datetime-picker__button': {
    padding: '0px 4px',
    opacity: '0.5',
  },
  '& .react-datetime-picker__inputGroup__input': {
    color: '#334155',
    fontSize: '14px',
  },
  '& .react-datetime-picker__inputGroup__input:invalid': {
    background: 'transparent',
  },
  '& .react-calendar': {
    position: 'fixed',
    zIndex: 1000,
  },
});

const FilterGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const FilterLabel = styled.label({
  fontSize: '11px',
  fontWeight: 500,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

const SearchInput = styled.input({
  // padding: '8px 12px',
  padding: '11px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#334155',
  backgroundColor: '#fff',
  outline: 'none',
  minWidth: '200px',
  '&:focus': {
    borderColor: '#10416D',
  },
  '&::placeholder': {
    color: '#94a3b8',
  },
});

const FilterSelect = styled.select({
  // padding: '8px 12px',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '14px',
  color: '#334155',
  backgroundColor: '#fff',
  outline: 'none',
  minWidth: '120px',
  cursor: 'pointer',
  '&:focus': {
    borderColor: '#10416D',
  },
});

const ActionButton = styled(Button)({
  backgroundColor: '#10416D',
  color: '#fff',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  padding: '8px 20px',
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: '#10416D',
  },
  '&:disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
  },
});

const ClearButton = styled(Button)({
  backgroundColor: '#fff',
  color: '#334155',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  padding: '8px 20px',
  borderRadius: '6px',
  marginTop: '20px',
  border: '1px solid #e2e8f0',
  '&:hover': {
    backgroundColor: '#f8fafc',
  },
});

// Content wrapper
const ContentWrapper = styled.div({
  padding: '24px',
});

// Summary cards
const SummaryCard = styled(Card)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
});

// Table card
const TableCard = styled(Paper)({
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  '& .MuiDataGrid-root': {
    border: 'none',
    borderRadius: '0px !important',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .MuiDataGrid-cell': {
    fontSize: '14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: '#f8fafc',
  },
});

// Issue circle
const IssueCircle = styled.div({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid #f59e0b',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#f59e0b',
  fontSize: '12px',
  fontWeight: 700,
  '&::after': {
    content: '"!"',
  },
});

const issueFilterValues = [
  { title: 'All Rooms', value: 'all' },
  { title: 'With Issues', value: 'true' },
];

const Analytics = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Pagination states
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filter states
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [search, setSearch] = useState('');
  const [hasIssuesFilter, setHasIssuesFilter] = useState('all');

  const deferredSearch = useDeferredValue(search);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const summaryParams = {};
      const roomsParams = {
        page: pageNumber,
        limit: pageSize
      };

      if (fromDate && toDate) {
        const startDate = moment(fromDate).format('YYYY-MM-DD');
        const endDate = moment(toDate).format('YYYY-MM-DD');
        roomsParams.startDate = startDate;
        roomsParams.endDate = endDate;
        summaryParams.startDate = startDate;
        summaryParams.endDate = endDate;
      }

      if (hasIssuesFilter === 'true') {
        roomsParams.hasIssues = 'true';
      }

      if (deferredSearch) {
        roomsParams.search = deferredSearch;
      }

      const summaryRes = await axios.get('/analytics/summary', { params: summaryParams });
      setSummary(summaryRes.data.summary);

      const roomsRes = await axios.get('/analytics/rooms', { params: roomsParams });

      let filteredRooms = roomsRes.data.rooms || [];
      if (deferredSearch && !roomsParams.search) {
        const searchLower = deferredSearch.toLowerCase();
        filteredRooms = filteredRooms.filter(room =>
          room.room_name?.toLowerCase().includes(searchLower) ||
          room.room_sid?.toLowerCase().includes(searchLower)
        );
      }

      setRooms(filteredRooms);
      setTotalRows(roomsRes.data.pagination?.total || filteredRooms.length);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
  };

  const handleToDateChange = (value) => {
    setToDate(value);
  };

  const onChangeSearch = (searchValue) => {
    setSearch(searchValue.trim());
  };

  const onChangeIssueFilter = (event) => {
    setHasIssuesFilter(event.target.value);
  };

  const handleAllClear = () => {
    setFromDate('');
    setToDate('');
    setSearch('');
    setHasIssuesFilter('all');
    setPageNumber(1);
  };

  const onChangePageSize = (newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const onPageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post('/analytics/sync', { daysBack: 1 });
      await fetchData();
    } catch (err) {
      console.error('Error syncing analytics:', err);
      setError('Failed to trigger sync job.');
    } finally {
      setSyncing(false);
    }
  };

  const handleRowClick = (params) => {
    const meetingId = params.row.room_name;
    if (meetingId) {
      navigate(`/room-logs/${meetingId}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, deferredSearch, hasIssuesFilter, pageNumber, pageSize]);

  const columns = [
    { field: 'room_name', headerName: 'Meeting ID', width: 250, sortable: false },
    {
      field: 'created_time',
      headerName: 'Date',
      width: 200,
      sortable: false,
      valueFormatter: (value) => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    { field: 'duration_seconds', headerName: 'Duration (s)', width: 150, sortable: false },
    { field: 'participant_count', headerName: 'Participants', width: 150, sortable: false },
    // {
    //   field: 'participants_with_issues',
    //   headerName: 'Issues',
    //   width: 100,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const count = params.value;
    //     return count > 0 ? (
    //       <Tooltip title={`${count} participant(s) with issues`}>
    //         <IssueCircle />
    //       </Tooltip>
    //     ) : (
    //       <CheckCircleOutline sx={{ color: '#16a34a', fontSize: 20 }} />
    //     );
    //   }
    // },
    {
      field: 'issue_types',
      headerName: 'Issue Types',
      width: 300,
      sortable: false,
      valueGetter: (value, row) => {
        const issueTypes = row?.issue_types;
        return issueTypes && Array.isArray(issueTypes) ? issueTypes.join(', ') : '-';
      }
    }
  ];

  if (loading && !summary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MainContainer>
      {/* Header Bar */}
      <HeaderBar>
        <HeaderLeft>
          <LogoImage src={logo} alt="Dialectica" />
        </HeaderLeft>
        <HeaderRight>
          <HeaderTitle>ANALYTICS</HeaderTitle>
          <ActionButton
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </ActionButton>
        </HeaderRight>
      </HeaderBar>

      {/* Filter Bar */}
      <FilterBar>
        <FilterGroup>
          <FilterLabel>FROM DATE</FilterLabel>
          <DateTimePicker
            name='fromDate'
            value={fromDate}
            onChange={handleFromDateChange}
            format='dd/MM/y'
            className='datepicker'
            disableClock
            required
            monthPlaceholder="MM"
            dayPlaceholder="DD"
            yearPlaceholder="YYYY"
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>TO DATE</FilterLabel>
          <DateTimePicker
            name='toDate'
            value={toDate}
            onChange={handleToDateChange}
            format='dd/MM/y'
            className='datepicker'
            disableClock
            required
            monthPlaceholder="MM"
            dayPlaceholder="DD"
            yearPlaceholder="YYYY"
          />
        </FilterGroup>

        <FilterGroup style={{ flex: 1, minWidth: '200px' }}>
          <FilterLabel>Meeting ID / Room SID</FilterLabel>
          <SearchInput
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(event) => { onChangeSearch(event.target.value) }}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Issue Filter</FilterLabel>
          <FilterSelect
            value={hasIssuesFilter}
            onChange={onChangeIssueFilter}
          >
            {issueFilterValues.map(option =>
              <option key={option.value} value={option.value}>{option.title}</option>
            )}
          </FilterSelect>
        </FilterGroup>

        <ClearButton onClick={handleAllClear}>
          Clear All
        </ClearButton>
      </FilterBar>

      {error && <Alert severity="error" sx={{ m: 3, mb: 0 }}>{error}</Alert>}

      {/* Content */}
      <ContentWrapper>
        {/* Summary Cards */}
        {summary && (
          <>
            {/* Row 1: Meeting Stats */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Total Rooms</Typography>
                    <Typography variant="h5" sx={{ color: '#10416D' }}>{summary.totalMeetings}</Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Successful</Typography>
                    <Typography variant="h5" sx={{ color: '#16a34a' }}>{summary.successfulMeetings || (summary.totalMeetings - (summary.meetingsWithIssues || 0))}</Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>With Issues</Typography>
                    <Typography variant="h5" sx={{ color: '#dc2626' }}>
                      {summary.meetingsWithIssues || 0}
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              {/* <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Success Rate</Typography>
                    <Typography variant="h5" sx={{ color: (summary.successRate || 100) >= 90 ? '#16a34a' : '#f59e0b' }}>
                      {summary.successRate || 100}%
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid> */}
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Total Errors</Typography>
                    <Typography variant="h5" sx={{ color: '#dc2626' }}>
                      {summary.totalErrors || 0}
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Total Participants</Typography>
                    <Typography variant="h5" sx={{ color: '#10416D' }}>{summary.totalParticipants}</Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Participants Success</Typography>
                    <Typography variant="h5" sx={{ color: '#16a34a' }}>
                      {(summary.totalParticipants || 0) - (summary.participantsWithIssues || 0)}
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Participants with Issues</Typography>
                    <Typography variant="h5" sx={{ color: '#dc2626' }}>
                      {summary.participantsWithIssues}
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <SummaryCard>
                  <CardContent sx={{ py: 2 }}>
                    <Typography color="textSecondary" gutterBottom sx={{ fontSize: '11px', textTransform: 'uppercase' }}>Total Warnings</Typography>
                    <Typography variant="h5" sx={{ color: '#f59e0b' }}>
                      {summary.totalWarnings || 0}
                    </Typography>
                  </CardContent>
                </SummaryCard>
              </Grid>
            </Grid>

            {/* Row 2: Participant Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
            </Grid>
          </>
        )}

        {/* Table */}
        <TableCard elevation={0}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#10416D' }}>Room History</Typography>
            <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.5 }}>
              Click on a row to view room activity logs
            </Typography>
          </Box>
          <DataGridPro
            rows={rooms}
            columns={columns}
            getRowId={(row) => row.room_sid}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={totalRows}
            page={pageNumber - 1}
            pageSize={pageSize}
            pageSizeOptions={[25, 50, 100]}
            onPaginationModelChange={({ page, pageSize: newPageSize }) => {
              if (newPageSize !== pageSize) {
                onChangePageSize(newPageSize);
              } else {
                onPageChange(page);
              }
            }}
            onRowClick={handleRowClick}
            autoHeight
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </TableCard>
      </ContentWrapper>
    </MainContainer>
  );
};

export default Analytics;


